import type {
  Column,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  RowGroupOpenedEvent,
  StateUpdatedEvent,
  ViewportChangedEvent,
} from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { Observable, ReplaySubject, asyncScheduler, throttleTime } from 'rxjs';
import { safeGridApi } from '../components/BlotterTable/utils';
import { useCallbackRef } from './useCallbackRef';
import { useConstant } from './useConstant';
import type { VisibleCellsChangedOutput } from './useVisibleCellsChanged.types';

export interface UseVisibleCellsChangedParams<T> {
  /** The unique key of each row, essentially just whatever the rowID is */
  getUniqueKey: (item: T) => string;
  /**
   * Callback for determining if a displayed column is relevant.
   *
   * Only relevant columns are represented in the output of this pipe.
   */
  isColumnRelevant: (column: Column) => boolean;
}

/**
 * This hook takes in several inputs from AgGrid events and translates that into an observable which will
 * inform consumers about what rendered items and visible columns are present in the connected blotter.
 *
 * The output observable is throttled at 200ms.
 *
 * The output observable returns absolute data sets as well as delta data sets.
 *
 * For proper function, all three inputs need to be correctly hooked up to the given blotter table.
 */
export const useVisibleCellsChanged = <T,>({
  getUniqueKey,
  isColumnRelevant,
}: UseVisibleCellsChangedParams<T>): {
  gridOptionsOverlay: RequireKeys<
    GridOptions<T>,
    'onViewportChanged' | 'onFilterChanged' | 'onRowGroupOpened' | 'onStateUpdated'
  >;
  visibleCellsChangedObs: Observable<VisibleCellsChangedOutput<T>>;
} => {
  /* ----------- INPUTS ------------ */
  const viewportChangedSubject = useConstant(new ReplaySubject<{ api: GridApi<T> }>());
  const isColumnRelevantDynamic = useCallbackRef(isColumnRelevant);

  const handleViewportChanged = useCallbackRef((event: ViewportChangedEvent<T>) => {
    viewportChangedSubject.next(event);
  });

  // There is a bug in aggrid where when you open some groups the viewportChanged event doesnt get called even tho the docs say
  // "called whenever the nodes mounted in the dom change". so we have to both listen to the viewportChanged event _and_ the rowgroupsopened one to cover all bases......... :(
  const handleRowGroupOpened = useCallbackRef((event: RowGroupOpenedEvent<T>) => {
    viewportChangedSubject.next(event);
  });

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent<T>) => {
    viewportChangedSubject.next({ api: event.api });
  });

  const handleAgGridFilterChanged = useCallbackRef((event: FilterChangedEvent) => {
    if (!event) {
      return;
    }
    viewportChangedSubject.next({ api: event.api });
  });

  /* ------------ OUTPUT OBSERVABLE ------------ */

  const visibleCellsChangedObs = useMemo(
    () =>
      new Observable<VisibleCellsChangedOutput<T>>(output => {
        let renderedItems: T[] = [];
        let renderedItemsMap: Map<string, T> = new Map();
        let visibleColumns: Column[] = [];
        let visibleColumnsMap: Map<string, Column> = new Map();

        const viewportChangedSub = viewportChangedSubject
          .pipe(throttleTime(200, asyncScheduler, { leading: false, trailing: true }))
          .subscribe(({ api: unsafeApi }) => {
            const api = safeGridApi(unsafeApi);
            if (!api) {
              return;
            }

            const newRenderedItems = compact(api?.getRenderedNodes().map(node => node.data));
            const newRenderedItemsMap = new Map(newRenderedItems.map(item => [getUniqueKey(item), item]));

            const removedItems = renderedItems
              .filter(item => !newRenderedItemsMap.has(getUniqueKey(item)))
              .filter(item => api.getRowNode(getUniqueKey(item)) != null);
            const addedItems = newRenderedItems.filter(item => !renderedItemsMap.has(getUniqueKey(item)));

            renderedItems = newRenderedItems;
            renderedItemsMap = newRenderedItemsMap;

            const newVisibleColumns = api.getAllDisplayedColumns().filter(column => isColumnRelevantDynamic(column));
            const newVisibleColumnsMap = new Map(newVisibleColumns.map(column => [column.getColId(), column]));

            const removedColumns = visibleColumns.filter(col => !newVisibleColumnsMap.has(col.getColId()));
            const addedColumns = newVisibleColumns.filter(col => !visibleColumnsMap.has(col.getColId()));

            visibleColumns = newVisibleColumns;
            visibleColumnsMap = newVisibleColumnsMap;

            output.next({
              renderedItems,
              renderedItemsMap,
              addedItems,
              removedItems,

              visibleColumns,
              visibleColumnsMap,
              addedColumns,
              removedColumns,
            });
          });

        return () => {
          viewportChangedSub.unsubscribe();
        };
      }),
    [viewportChangedSubject, getUniqueKey, isColumnRelevantDynamic]
  );

  return {
    gridOptionsOverlay: {
      onFilterChanged: handleAgGridFilterChanged,
      onViewportChanged: handleViewportChanged,
      onRowGroupOpened: handleRowGroupOpened,
      onStateUpdated: handleGridStateUpdated,
    },
    visibleCellsChangedObs,
  };
};
